import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isApiError } from "@/data/api/client";
import { partnerReservationsRepo } from "@/data/repos/partnerReservationsRepo";

export const reservationsQueryKey = ["partner-reservations"] as const;
export const partnerOffersQueryKey = ["partner-offers"] as const;
export const todayQueryKey = ["partner-today"] as const;

const CONFIRMED_CODE_TOAST_MS = 15_000;

interface AnswerCase {
  code: string;
  toast: "info" | "error";
  message: string;
}

/** The business's three answers to a booking. Every one of them changes how
 *  many places a sleva has left, so every surface that counts them is refreshed
 *  together. */
export function useReservationAnswers() {
  const queryClient = useQueryClient();
  const [captureIncident, setCaptureIncident] = useState<number | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: reservationsQueryKey });
    void queryClient.invalidateQueries({ queryKey: partnerOffersQueryKey });
    void queryClient.invalidateQueries({ queryKey: todayQueryKey });
  };

  const dismissCaptureIncident = () => setCaptureIncident(null);

  // A 409 the API named is a decision, not a malfunction — it is always
  // reported for what it is (news the row moved, or a defect worth naming) and
  // the row is refreshed either way. Only a code this table doesn't cover falls
  // back to the generic retry message.
  const reportAnswerError = (error: unknown, cases: AnswerCase[], failureMessage: string) => {
    const matched = cases.find((answerCase) => isApiError(error, answerCase.code));
    if (matched) {
      toast[matched.toast](matched.message);
      refresh();
      return;
    }
    toast.error(failureMessage);
  };

  const confirm = useMutation({
    mutationFn: (reservationId: number) => partnerReservationsRepo.confirm(reservationId),
    onSuccess: (confirmed) => {
      // The row leaves the queue on the refetch and history is not built yet, so
      // this toast is the only place the portal shows the code. It may still
      // expire: the customer is sent the same code the moment we confirm, and
      // the one urgent use is dictating it to somebody standing at the counter.
      toast.success(
        confirmed.code
          ? `Rezervace potvrzena. Kód ${confirmed.code} zákazník ukáže při příchodu a už ho má v potvrzení — přečtěte mu ho, jen když stojí před vámi.`
          : "Rezervace potvrzena.",
        { duration: CONFIRMED_CODE_TOAST_MS, closeButton: true },
      );
      refresh();
    },
    onError: (error, reservationId) => {
      if (isApiError(error, "CAPTURED_NOT_RECORDED")) {
        // Money already moved and no row says so. Never a retry — capturing a
        // second time captures nothing and repeats the same error — and never a
        // toast that can vanish on its own: it stays until someone dismisses it.
        setCaptureIncident(reservationId);
        refresh();
        return;
      }
      reportAnswerError(
        error,
        [
          // Not held any more — answered elsewhere, or swept while this partner
          // was looking at it. That is news the row moved, not a failure.
          {
            code: "NOT_HELD",
            toast: "info",
            message:
              "Tuto rezervaci už nelze potvrdit — mezitím se změnil její stav. Seznam jsme aktualizovali.",
          },
          // Held, but with nothing to capture — a defect in how the reservation
          // was made, not a race with another answer, so it is reported instead
          // of being folded into the generic retry message.
          {
            code: "NO_AUTHORIZATION",
            toast: "error",
            message:
              "Tuto rezervaci nejde potvrdit — chybí záznam o autorizaci platby. Nahlaste to prosím podpoře.",
          },
        ],
        "Rezervaci se nepodařilo potvrdit. Zkuste to prosím znovu.",
      );
    },
  });

  const reject = useMutation({
    mutationFn: (reservationId: number) => partnerReservationsRepo.reject(reservationId),
    onSuccess: () => {
      toast.success("Rezervace odmítnuta. Kapacita slevového termínu je zase volná.");
      refresh();
    },
    onError: (error) =>
      reportAnswerError(
        error,
        [
          // /reject reads no deadline either — it finds the row no longer held
          // and cannot know why, so it must not guess.
          {
            code: "NOT_HELD",
            toast: "info",
            message:
              "Tuto rezervaci už nelze odmítnout — mezitím se změnil její stav. Seznam jsme aktualizovali.",
          },
        ],
        "Rezervaci se nepodařilo odmítnout. Zkuste to prosím znovu.",
      ),
  });

  const cancel = useMutation({
    mutationFn: (reservationId: number) => partnerReservationsRepo.cancel(reservationId),
    onSuccess: () => {
      toast.success("Rezervace zrušena. Zákazníkovi nic nestrhneme, jeho částku uvolníme zpět.");
      refresh();
    },
    onError: (error) =>
      reportAnswerError(
        error,
        [{ code: "NOT_CANCELLABLE", toast: "info", message: "Tuto rezervaci už zrušit nelze." }],
        "Rezervaci se nepodařilo zrušit. Zkuste to prosím znovu.",
      ),
  });

  return { confirm, reject, cancel, refresh, captureIncident, dismissCaptureIncident };
}
